import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Chrome, Eye, EyeOff, Loader2, AlertCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { cx } from "@/components/site/primitives";
import { useAuth } from "@/lib/useAuth";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_RE = /^[a-zA-Z0-9_]+$/;
const INITIAL_VALUES = { displayName: "", username: "", email: "", password: "", confirmPassword: "" };

function validateField(name, value, values) {
  const trimmed = value.trim();
  if (name === "displayName" && !trimmed) return "Display name is required.";
  if (name === "username") {
    if (!trimmed) return "Username is required.";
    if (trimmed.length < 3 || trimmed.length > 20) return "Use 3 to 20 characters.";
    if (!USERNAME_RE.test(trimmed)) return "Use letters, numbers, or underscores only.";
  }
  if (name === "email") {
    if (!trimmed) return "Email is required.";
    if (!EMAIL_RE.test(trimmed)) return "Enter a valid email address.";
  }
  if (name === "password") {
    if (!value) return "Password is required.";
    if (value.length < 8) return "Use at least 8 characters.";
  }
  if (name === "confirmPassword") {
    if (!value) return "Please confirm your password.";
    if (value !== values.password) return "Passwords do not match.";
  }
  return "";
}

function strengthFor(password) {
  if (!password) return { label: "", level: 0, tone: "bg-line" };
  let score = 0;
  if (password.length >= 8) score += 1;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  if (score <= 1) return { label: "Weak", level: 1, tone: "bg-coral" };
  if (score <= 2) return { label: "Getting there", level: 2, tone: "bg-yellow" };
  if (score === 3) return { label: "Good", level: 3, tone: "bg-sage" };
  return { label: "Strong", level: 4, tone: "bg-blue" };
}

export default function SignUpForm() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState(INITIAL_VALUES);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [authError, setAuthError] = useState("");
  const [status, setStatus] = useState("idle");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const strength = strengthFor(values.password);
  const updateValue = (name, value) => {
    const nextValues = { ...values, [name]: value };
    setValues(nextValues);
    setAuthError("");
    if (touched[name]) setErrors((previous) => ({ ...previous, [name]: validateField(name, value, nextValues) }));
    if (name === "password" && touched.confirmPassword) {
      setErrors((previous) => ({ ...previous, confirmPassword: validateField("confirmPassword", nextValues.confirmPassword, nextValues) }));
    }
  };

  const touchField = (name) => {
    setTouched((previous) => ({ ...previous, [name]: true }));
    setErrors((previous) => ({ ...previous, [name]: validateField(name, values[name], values) }));
  };

  const validate = () => {
    const nextErrors = Object.keys(values).reduce((all, name) => {
      const message = validateField(name, values[name], values);
      if (message) all[name] = message;
      return all;
    }, {});
    setErrors(nextErrors);
    setTouched(Object.keys(values).reduce((all, name) => ({ ...all, [name]: true }), {}));
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setAuthError("");
    if (status === "loading" || !validate()) return;
    setStatus("loading");
    try {
      await signUp({ ...values, displayName: values.displayName.trim(), username: values.username.trim().toLowerCase() });
      navigate("/signup/success", { replace: true });
    } catch (error) {
      setStatus("error");
      setAuthError(error.message || "We could not create your account. Please try again.");
    }
  };

  const fields = [
    { name: "displayName", label: "Display name", placeholder: "What should we call you?", type: "text", autoComplete: "name" },
    { name: "username", label: "Username", placeholder: "yourreadinghandle", type: "text", autoComplete: "username" },
    { name: "email", label: "Email", placeholder: "you@scribblereads.app", type: "email", autoComplete: "email" },
  ];

  return (
    <motion.form onSubmit={handleSubmit} noValidate className="w-full" data-testid="signup-form">
      <AnimatePresence>
        {authError && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-4 flex items-start gap-2.5 rounded-xl border border-coral/30 bg-coral/10 px-4 py-3 text-sm text-coral" role="alert" data-testid="signup-error-banner">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{authError}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-3.5">
        {fields.map((field) => (
          <div key={field.name}>
            <Label htmlFor={`signup-${field.name}`} className="text-ink">{field.label} <span className="text-coral" aria-hidden="true">*</span></Label>
            <Input id={`signup-${field.name}`} type={field.type} autoComplete={field.autoComplete} placeholder={field.placeholder} value={values[field.name]} onChange={(event) => updateValue(field.name, event.target.value)} onBlur={() => touchField(field.name)} aria-invalid={!!errors[field.name]} aria-describedby={errors[field.name] ? `signup-${field.name}-error` : undefined} className={cx("mt-1.5 h-10 rounded-xl border-line bg-cream/70 px-4 text-sm text-ink placeholder:text-subtle/70 focus-visible:border-coral focus-visible:ring-1 focus-visible:ring-coral", errors[field.name] && "border-coral")} />
            {errors[field.name] && <p id={`signup-${field.name}-error`} className="mt-1 text-xs font-medium text-coral">{errors[field.name]}</p>}
          </div>
        ))}

        <div>
          <Label htmlFor="signup-password" className="text-ink">Password <span className="text-coral" aria-hidden="true">*</span></Label>
          <div className="relative mt-1.5">
            <Input id="signup-password" type={showPassword ? "text" : "password"} autoComplete="new-password" placeholder="At least 8 characters" value={values.password} onChange={(event) => updateValue("password", event.target.value)} onBlur={() => touchField("password")} aria-invalid={!!errors.password} aria-describedby="signup-password-hint signup-password-error" className={cx("h-10 rounded-xl border-line bg-cream/70 px-4 pr-11 text-sm text-ink placeholder:text-subtle/70 focus-visible:border-coral focus-visible:ring-1 focus-visible:ring-coral", errors.password && "border-coral")} />
            <button type="button" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-subtle hover:text-coral focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral">{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
          </div>
          {values.password && (
            <div id="signup-password-hint" className="mt-2 flex items-center gap-2" aria-live="polite">
              <div className="flex flex-1 gap-1" aria-label={strength.label ? `Password strength: ${strength.label}` : "Password strength"}>{[1, 2, 3, 4].map((part) => <span key={part} className={cx("h-1 flex-1 rounded-full", part <= strength.level ? strength.tone : "bg-line")} />)}</div>
              {strength.label && <span className="text-[11px] font-semibold text-subtle">{strength.label}</span>}
            </div>
          )}
          {errors.password && <p id="signup-password-error" className="mt-1 text-xs font-medium text-coral">{errors.password}</p>}
        </div>

        <div>
          <Label htmlFor="signup-confirm-password" className="text-ink">Confirm password <span className="text-coral" aria-hidden="true">*</span></Label>
          <div className="relative mt-1.5">
            <Input id="signup-confirm-password" type={showConfirm ? "text" : "password"} autoComplete="new-password" placeholder="Type it again" value={values.confirmPassword} onChange={(event) => updateValue("confirmPassword", event.target.value)} onBlur={() => touchField("confirmPassword")} aria-invalid={!!errors.confirmPassword} aria-describedby={errors.confirmPassword ? "signup-confirm-password-error" : undefined} className={cx("h-10 rounded-xl border-line bg-cream/70 px-4 pr-11 text-sm text-ink placeholder:text-subtle/70 focus-visible:border-coral focus-visible:ring-1 focus-visible:ring-coral", errors.confirmPassword && "border-coral")} />
            <button type="button" onClick={() => setShowConfirm((visible) => !visible)} aria-label={showConfirm ? "Hide confirmed password" : "Show confirmed password"} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-subtle hover:text-coral focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral">{showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
          </div>
          {errors.confirmPassword && <p id="signup-confirm-password-error" className="mt-1 text-xs font-medium text-coral">{errors.confirmPassword}</p>}
        </div>

        <button type="submit" disabled={status === "loading"} data-testid="signup-submit" className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-coral-fill text-sm font-semibold text-[#FFF7F0] shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift disabled:cursor-not-allowed disabled:opacity-80 disabled:hover:translate-y-0">
          {status === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating your reading corner...</> : "Create Account"}
        </button>
      </div>

      <div className="my-5 flex items-center gap-3"><span className="h-px flex-1 bg-line" /><span className="text-[11px] font-medium uppercase tracking-wider text-subtle">or</span><span className="h-px flex-1 bg-line" /></div>
      <button type="button" onClick={() => toast("Google sign-up will be available when authentication is connected.")} data-testid="signup-google" className="flex h-11 w-full items-center justify-center gap-2.5 rounded-full border border-line bg-card text-sm font-semibold text-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-coral/50 hover:shadow-soft"><Chrome className="h-4 w-4" /> Continue with Google</button>
      <p className="mt-5 text-center text-sm text-subtle">Already have an account? <Link to="/signin" data-testid="signup-login-link" className="link-underline font-semibold text-coral">Log in</Link></p>
    </motion.form>
  );
}
